import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as toastr from 'toastr';

import policyActions from '../policy/policyActions';
import policyAPI, { defaultTrustee } from '../policy/policyAPI';

import PolicyBeneficiariesList from '../policy/PolicyBeneficiariesList';
import { Page, PageContent, PageHeader } from '../components/common';
import { Button } from '../components/buttons';
import { IconAdd } from '../components/icons';
import { ensureUserHasRole } from '../auth/authComponents';
import Utils, { SecurityRoles, UtilsBeneficiaries, BeneficiaryTypes } from '../common/utils';

interface Props { 
	className?: string;
	currentBeneficiaries: CustomerPolicyBeneficiaryDto[];
	pendingBeneficiaries: CustomerPolicyBeneficiaryDto[];
	policies: CustomerPolicy[];
	deleteSucceed: Boolean;
	actions: any;
	history: any;
	match: any;
}
interface State {
	policiesWithConfirmedBeneficiaries: CustomerPolicy[];
	policiesWithPendingBeneficiaries: CustomerPolicy[];
	loading: boolean;
	isDeleting: boolean;
}
class BeneficiariesPage extends React.Component<Props, State> {

	state = {
		policiesWithConfirmedBeneficiaries: [] as CustomerPolicy[],
		policiesWithPendingBeneficiaries: [] as CustomerPolicy[],
		loading: true,
		isDeleting: false,
	};

	componentDidMount() {
		this.loadData();
	}

	loadData = () => {
		const { userId } = this.props.match.params;
		const p1 = this.props.actions.fetchCustomerPolicies(userId);
		const p2 = this.props.actions.fetchCustomerPolicyBeneficiaries(userId);
		Promise.all([p1, p2])
		.then(() => {
			const currentPolicyIds = new Set();
			this.props.currentBeneficiaries.forEach(beneficiary => {
				currentPolicyIds.add(beneficiary.customerPolicyId);
			});

			const pendingPolicyIds = new Set();
			this.props.pendingBeneficiaries.forEach(beneficiary => {
				pendingPolicyIds.add(beneficiary.customerPolicyId);
			});

			const policiesWithConfirmedBeneficiaries = this.props.policies.filter(p => currentPolicyIds.has(p.id));
			const policiesWithPendingBeneficiaries = this.props.policies.filter(p => pendingPolicyIds.has(p.id));

			this.setState({ 
				policiesWithConfirmedBeneficiaries, 
				policiesWithPendingBeneficiaries,
				loading: false 
			});
		});
	}

	onEdit = (row: CustomerPolicyBeneficiaryDto) => {
		const policy = this.props.policies.find(p => p.id === row.customerPolicyId);
		this.props.actions.setCustomerPolicy(policy);
		this.props.history.push(`/user/${this.props.match.params.userId}/beneficiaries/${row.id}`);
	}

	onDelete = (row: CustomerPolicyBeneficiaryDto) => {
		if (this.state.isDeleting === true) {
			return;
		}
		this.setState({isDeleting: true});
		this.props.actions.deleteCustomerPolicyBeneficiary(row.id, row.statusId)
		.then(() => {
			if (this.props.deleteSucceed) {
				toastr.success('Beneficiary deleted!');
			} else {
				toastr.error('Each policy requires at least one beneficiary');
			}
			this.props.actions.fetchCustomerPolicyBeneficiaries(this.props.match.params.userId);
		})
		.then(() => {
			this.setState({isDeleting: false});
			this.loadData();
		});
	}

	onDownloadSignedForm = (customerId: string, policyId: string) => {
		policyAPI.fetchSignedBeneficiaryDocumentUrl(customerId, policyId)
		.then((url) => {
			window.open(url);
		});
	}

	onDownloadForm = (policyId: string, policy: CustomerPolicy) => {

		if (!this.validatePercentage(policy)) {
			toastr.error('Before you are able to download the beneficiary form, ' +
				'adjust your disbursements so they sum to 100%.');
			return;
		}

		var pendingPrimaryIds = this.props.pendingBeneficiaries
		.filter(b => b.typeId === BeneficiaryTypes.Primary && b.customerPolicyId === policyId).map(b => b.id);

		const tooBigMessage = 'Please <a href="/contact-us">contact support</a> for a document to sign.';
		const tooBigToastOptions: ToastrOptions = {timeOut: 0 , extendedTimeOut: 0};
		// There are only 4 rows for primary beneficiaries in the pdf.
		if (pendingPrimaryIds.length > 4) {
			toastr.info(tooBigMessage, '', tooBigToastOptions);
			return;
		}

		var pendingContingentIds = this.props.pendingBeneficiaries
		.filter(b => b.typeId === BeneficiaryTypes.Contingent && b.customerPolicyId === policyId).map(b => b.id);
		// There are only 4 rows for contingent beneficiaries in the pdf.
		if (pendingContingentIds.length > 4) {
			toastr.info(tooBigMessage, '', tooBigToastOptions);
			return;
		}

		this.props.actions.fetchCustomerPolicyBeneficiaryForm(this.props.match.params.userId, policyId, '1')
		.then(() => this.loadData());
	}

	validatePercentage = (policy: CustomerPolicy) => {
		const policyBeneficiaries = this.props.pendingBeneficiaries.filter(b => b.customerPolicyId === policy.id);
		return UtilsBeneficiaries.isDisbursementValid(policyBeneficiaries);
	}

	onManageDisbursement = (beneficiaries: CustomerPolicyBeneficiaryDto[], policy: CustomerPolicy, isCurrent: boolean) => {
		this.props.actions.setCustomerPolicy(policy);
		let filteredBeneficiaries: CustomerPolicyBeneficiaryDto[] = [];
		if (!isCurrent) {
			filteredBeneficiaries = this.props.pendingBeneficiaries
																	.filter(b => b.customerPolicyId === policy.id);
			this.props.actions.setCustomerPolicyBeneficiaries(filteredBeneficiaries);
			
		} else {
			filteredBeneficiaries = this.props.currentBeneficiaries
																	.filter(b => b.customerPolicyId === policy.id);
			this.props.actions.setCustomerPolicyBeneficiaries(filteredBeneficiaries);
		}
		if (filteredBeneficiaries.some(b => b.isUnderage === true)) {
			const trustee = filteredBeneficiaries.find(b => b.typeId === BeneficiaryTypes.Trustee);
			if (trustee) {
				this.props.actions.setTrustee(trustee);
			} else {
				let newTrustee = defaultTrustee;
				this.props.actions.setTrustee(newTrustee);
			}
		}
		this.props.history.push(`/user/${this.props.match.params.userId}/beneficiaries-disbursement`);
	}

	renderCurrentPoliciesList = () => {
		return (
			<div className="mb-4">
				<h3>Current</h3>
				{this.renderPoliciesList(this.state.policiesWithConfirmedBeneficiaries, this.props.currentBeneficiaries, true)}
			</div>
		);
	}
	renderPendingPoliciesList = () => {
		return this.props.pendingBeneficiaries.length > 0 && (
			<div>
				<h3>Pending</h3>
				{this.renderPoliciesList(this.state.policiesWithPendingBeneficiaries, this.props.pendingBeneficiaries)}
			</div>
		);
	}
	renderPoliciesList = (
		policies: CustomerPolicy[], 
		beneficiaries: CustomerPolicyBeneficiaryDto[], 
		isCurrent: boolean = false,
	) => {
		return (
			<ul className="list-unstyled mb-0 customer-dashboard-plans">
				{policies.length > 0 ? (
					policies.map(policy => {
						// disable editing on current policy beneficiaries that have pending changes
						const readOnly = isCurrent 
							&& this.props.pendingBeneficiaries.filter(b => b.customerPolicyId === policy.id).length > 0;

						return (<li key={policy.id} className="customer-dashboard-plan">
							<div className="customer-dashboard-plan-info">
								{this.renderPolicyHeader(policy, isCurrent)}
								{beneficiaries.filter(b => b.customerPolicyId === policy.id).length > 0 &&
									<p className="d-inline">
										<Button 
											className="btn-outline-primary" 
											disabled={readOnly} 
											onClick={() => this.onManageDisbursement(beneficiaries, policy, isCurrent)}
										>
											<span className="align-middle mx-2">Manage disbursement</span>
										</Button>
									</p>}
							</div>
							{this.renderBeneficiaryList(beneficiaries, policy.id!, readOnly)}
						</li>);
					})
				) : (
						<li>
							<em className="reduced-em">None confirmed</em>
						</li>
					)}
			</ul>
		);
	}
	renderBeneficiaryList = (beneficiaries: CustomerPolicyBeneficiaryDto[], policyId: string, readOnly: boolean) => {
		const filteredBeneficiaries = beneficiaries
		.filter(b => b.customerPolicyId === policyId && b.typeId !== BeneficiaryTypes.Trustee);
		return (
			<PolicyBeneficiariesList
				beneficiaries={filteredBeneficiaries}
				onEdit={this.onEdit}
				onDelete={this.onDelete}
				loading={this.state.loading}
				readOnly={readOnly}
			/>
		);
	}

	renderPolicyHeader = (policy: CustomerPolicy, isCurrent: boolean) => {
		return (
				<span className="d-inline large">
					<strong>
						Policy {policy.number} {!isCurrent && ('(' + Utils.getPickListItemDescription(policy.beneficiariesStatus) + ')')}
					</strong>
					{isCurrent ? '' : 
					<span>
						<Button className="btn-outline-secondary ml-2" onClick={() => this.onDownloadForm(policy.id!, policy!)}>
							<em>Print Beneficiary Form</em>
						</Button>
					</span>}
				</span>
		);
	}

	render() {
		return (
			<Page title="Beneficiaries">
				<PageContent>
					<PageHeader title="Your beneficiaries">
						<p>
							To designate or change your beneficiaries, please complete the beneficiary 
							designation form and mail us a signed copy. Once we receive the form, it will 
							be effective from the date signed, provided we have not yet paid a death benefit.
						</p>
						<p className="mb-0">
							<strong>If we have not received a beneficiary designation form from you,
								the beneficiary of your policy will be your estate.</strong>
						</p>
					</PageHeader>
					{this.renderCurrentPoliciesList()}
					{this.renderPendingPoliciesList()}
					<div className="workflow-progress-actions mt-4">
						<Button
							to={`/user/${this.props.match.params.userId}/beneficiaries/new`}
							className="btn-outline-primary workflow-progress-action"
						>
							<span className="align-middle mx-2">Add a beneficiary</span>
							<IconAdd />
						</Button>
					</div>
				</PageContent>
			</Page>
		);
	}
}
function mapStateToProps(state: any, props: Props) {
	const currentBeneficiaries = state.policy.beneficiaries.current
	.sort(UtilsBeneficiaries.sortPrimaryThenByName);
	const pendingBeneficiaries = state.policy.beneficiaries.pending
	.sort(UtilsBeneficiaries.sortPrimaryThenByName);
	return {
		currentBeneficiaries,
		pendingBeneficiaries,
		policies: state.policy.policies,
		deleteSucceed: state.policy.deleteSucceed,
	};
}
function mapDispatchToProps(dispatch: any) {
	const actions = { ...policyActions };
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}
const ConnectedPage = connect(mapStateToProps, mapDispatchToProps)(BeneficiariesPage);
export default ensureUserHasRole(ConnectedPage as any, [SecurityRoles.Customer]);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/user/BeneficiariesPage.tsx